import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import type { SearchSuggestion } from '../types';
import type { BudgetApiClient } from '../api/client';
import { useT } from '../i18n';

const Container = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;
`;

// Figma "dashed pill" search bar — transparent fill, 1 px dashed border in
// `--color-text-default`. The focus signal switches to a solid border + soft
// focus ring rather than a heavy gradient/shadow.
const InputWrapper = styled.div<{ isFocused: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  background: transparent;
  border-radius: 999px;
  border: 1px ${props => props.isFocused ? 'solid' : 'dashed'} ${theme.colors.text.primary};
  box-shadow: ${props => props.isFocused
    ? '0 0 0 3px var(--color-focus-ring)'
    : 'none'};
  transition: border-style 0.2s ease, box-shadow 0.2s ease;
`;

const SearchInput = styled.input`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  padding: 0.4em 2.6em;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${theme.colors.text.primary};
  outline: none;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const IconWrapper = styled.span<{ isFocused: boolean }>`
  position: absolute;
  left: 16px;
  display: flex;
  align-items: center;
  /* Drives the inline SearchIcon SVG, which is sized in em. */
  font-size: 18px;
  color: ${props => props.isFocused ? theme.colors.text.primary : theme.colors.text.muted};
  transition: color 0.2s ease;
`;

const ClearButton = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 16px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--color-bg-muted);
    color: ${theme.colors.text.primary};
  }
`;

const Dropdown = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  margin: 0;
  padding: ${theme.spacing.sm};
  list-style: none;
  background: ${theme.colors.surface};
  border: 1px solid rgba(24, 28, 68, 0.08);
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 12px 32px rgba(24, 28, 68, 0.15), 0 4px 8px rgba(0, 0, 0, 0.05);
  max-height: 320px;
  overflow-y: auto;
  // Sits above the FilterCard's z-index 1000 stacking context so search
  // suggestions overlay the filter bar that lives below it on the page.
  z-index: 1100;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DropdownItem = styled.li<{ isHighlighted: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px ${theme.spacing.md};
  cursor: pointer;
  background: ${props => props.isHighlighted ? `linear-gradient(135deg, rgba(24, 28, 68, 0.06) 0%, rgba(24, 28, 68, 0.03) 100%)` : 'transparent'};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 2px;
  transition: all 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(24, 28, 68, 0.06) 0%, rgba(24, 28, 68, 0.03) 100%);
  }

  &:active {
    background: linear-gradient(135deg, rgba(24, 28, 68, 0.1) 0%, rgba(24, 28, 68, 0.05) 100%);
  }
`;

const ItemLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

// Typography unified with the SearchableSelect dropdown options:
// sans-stack, 0.8125rem, regular weight. The code line keeps the muted
// colour for hierarchy against the description below it; the count is
// flattened to plain muted inline text (no pill) to mirror the dropdown.
const ItemCode = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: var(--color-text-muted);
  line-height: 1.3;
`;

const ItemDescription = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: var(--color-text-default);
  line-height: 1.3;
`;

const ItemCount = styled.span`
  font-family: var(--sans-stack);
  font-size: 1rem;
  color: var(--color-text-muted);
  flex-shrink: 0;
  margin-left: ${theme.spacing.sm};
`;

const LoadingSpinner = styled.span`
  position: absolute;
  right: 14px;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(24, 28, 68, 0.1);
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Search glyph. The design system's icon font (Material Symbols Outlined) does
// not reliably ship the "search" glyph in this widget's context, so we inline
// the official Material Symbols "search" SVG from fonts.google.com/icons. Sized
// in `em` so the wrapper's font-size controls it; `currentColor` picks up the
// wrapper's muted/focused colour.
const SearchIcon = () => (
  <svg width="1em" height="1em" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
    <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
  </svg>
);

/** Parse "(code) description" into { code, description } */
function parseLabelParts(label: string): { code: string; description: string } {
  const match = label.match(/^\(([^)]*)\)\s*(.*)$/);
  if (match) {
    return { code: match[1], description: match[2] };
  }
  return { code: '', description: label };
}

interface SearchAutocompleteProps {
  initialValue?: string;
  apiClient: BudgetApiClient;
  onSelect: (suggestion: SearchSuggestion | null) => void;
  placeholder?: string;
  budgetType?: 'E' | 'A';
}

export function SearchAutocomplete({
  initialValue = '',
  apiClient,
  onSelect,
  placeholder,
  budgetType = 'A',
}: SearchAutocompleteProps) {
  const m = useT();
  // Local input state - changes don't propagate to parent
  const [inputValue, setInputValue] = useState(initialValue);

  // Fetch suggestions internally
  const { suggestions, isLoading } = useSearchSuggestions(apiClient, inputValue, budgetType);

  // Sync with external initialValue when it changes (e.g., filter cleared)
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && suggestions.length > 0) {
      setIsOpen(true);
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        } else if (suggestions.length > 0) {
          // No item highlighted: pick the first suggestion. Previously we forwarded
          // the raw text as a "suggestion", but the server's q filter is an exact id
          // match — sending raw German text returned zero rows and blanked the chart.
          handleSelectSuggestion(suggestions[0]);
        }
        // No suggestions: do nothing. Don't fire a phantom filter that produces blank state.
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    // Show the label in the input, pass the full suggestion to onSelect
    setInputValue(suggestion.label);
    onSelect(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    setInputValue('');
    onSelect(null);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const showDropdown = isOpen && suggestions.length > 0;

  return (
    <Container ref={containerRef} data-filter="search">
      <InputWrapper isFocused={isFocused} data-filter="search-wrapper">
        <IconWrapper isFocused={isFocused} data-filter="search-icon">
          <SearchIcon />
        </IconWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            if (suggestions.length > 0) setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? m.search.placeholder}
          data-filter="search-input"
        />
        {isLoading ? (
          <LoadingSpinner data-filter="search-spinner" />
        ) : inputValue ? (
          <ClearButton onClick={handleClear} data-filter="search-clear">
            <span className="icon" data-icon="close" />
          </ClearButton>
        ) : null}
      </InputWrapper>

      <Dropdown isOpen={showDropdown} data-filter="search-suggestions">
        {suggestions.map((suggestion, index) => (
          <DropdownItem
            key={suggestion.value}
            isHighlighted={index === highlightedIndex}
            onClick={() => handleSelectSuggestion(suggestion)}
            onMouseEnter={() => setHighlightedIndex(index)}
            data-filter="search-suggestion"
          >
            <ItemLabelWrapper data-filter="search-suggestion-label">
              {(() => {
                const displayLabel = suggestion.matchedLabel && suggestion.matchedLabel !== suggestion.label
                  ? suggestion.matchedLabel
                  : suggestion.label;
                const { code, description } = parseLabelParts(displayLabel);
                const codeParts = [
                  suggestion.einzelplan && m.search.einzelplan(suggestion.einzelplan),
                  suggestion.kapitel && m.search.kapitel(suggestion.kapitel),
                  code && `(${code})`,
                ].filter(Boolean).join(', ');
                return <>
                  {codeParts && <ItemCode>{codeParts}</ItemCode>}
                  <ItemDescription>{description}</ItemDescription>
                </>;
              })()}
            </ItemLabelWrapper>
            <ItemCount data-filter="search-suggestion-count">{suggestion.count}</ItemCount>
          </DropdownItem>
        ))}
      </Dropdown>
    </Container>
  );
}
